import React, { useEffect, useState } from 'react';
import {
  Container,
  Row,
  Col,
  Button,
  Spinner,
  Form,
  Modal,
} from 'react-bootstrap';
import { getDistance } from 'geolib';
import ListItems from './ListItems';
import { CONFIG } from '../utils/config';
import AgeSelect from './AgeSelelct';
import IndustrySelect from './IndustrySelelct';
import ListViewSelector from "./ListingViewSelector";
import MapDisplay from "./MapDisplay";
import { recursiveFetch } from '../utils/utils';
import IconReset from '../images/icon_reset.png';


const ListingDisplay = () => {
  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);
  const [show, setShow] = useState(false);

  const [viewStyle, setViewStyle] = useState('cards');
  const [sortedListings, setSortedListings] = useState([]);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nextLink, setNextLink] = useState(null);
  const [moreToDisplay, setMoreToDisplay] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  // search fields.
  const [textQueryData, setTextQueryData] = useState();
  const [textSearch, setTextSearch] = useState(null);
  const [postcodeSearch, setPostcodeSearch] = useState(null);
  const [industrySearch, setIndustrySearch] = useState('All categories');
  const [ageSearch, setAgeSearch] = useState('All ages');
  const [userLatLng, setUserLatLng] = useState({});
  const [filtersApplied, setFiltersApplied] = useState(false);

  const viewStyleCallback = (props) => {
    if(viewStyle !== props) {
      setViewStyle(props);
      setLoading(true);
    }
  }

  const fetchData = async () => {
    const nodesPerReq = viewStyle === 'map' ? 400 : postcodeSearch ? 50 : CONFIG.SHOW_ITEMS_NUMBER;
    let query = `${CONFIG.API_URL}${CONFIG.ORG_PATH}?page[limit]=${nodesPerReq}&sort=-changed,-created`

    // apply filters
    if (industrySearch !== 'All categories') {
      const industryFilter = `&filter[industry-filter][condition][path]=field_industry.name&filter[industry-filter][condition][operator]=IN&filter[industry-filter][condition][value][]=${industrySearch}`;
      query += industryFilter;
    }

    if (ageSearch !== 'All ages') {
      const filterVal = ageSearch === '19+' ? '19%2b' : ageSearch;
      const ageFilter = `&filter[age-filter][condition][path]=field_age_range.name&filter[age-filter][condition][operator]=IN&filter[age-filter][condition][value][]=${filterVal}`;
      query += ageFilter;
    }

    if (textSearch !== null) {
      const searchFilters = [
        `&filter[search-group][group][conjunction]=OR`,
        `&filter[title][operator]=CONTAINS&filter[title][value]=${textSearch}&filter[title][condition][memberOf]=search-group`,
        `&filter[field_description.value][operator]=CONTAINS&filter[field_description.value][value]=${textSearch}&filter[field_description.value][condition][memberOf]=search-group`,
        `&filter[field_contact_name][operator]=CONTAINS&filter[field_contact_name][value]=${textSearch}&filter[field_contact_name][condition][memberOf]=search-group`,
      ]

      query += searchFilters.join('');
    }

    // if user is searching by postcode we need to recursively fetch all nodes
    // they will then need to be sorted based on distance from users entered postcode
    if (postcodeSearch) {
      const nodes = await recursiveFetch(query);

      // get lat/lng from user entered postcode
      const geocodeUrl = `https://maps.googleapis.com/maps/api/geocode/json?address=${postcodeSearch}&key=${process.env.REACT_APP_G_API_KEY}`;
      const response = await fetch(geocodeUrl);
      const fetchedGeocodeData = await response.json();

      if (fetchedGeocodeData.status === 'OK' && fetchedGeocodeData?.results?.length > 0) {
        const location = fetchedGeocodeData.results[0].geometry.location
        const userLat = location?.lat;
        const userLng = location?.lng;

        // sort nodes based on distance between user coords and node item coords (closest to furthest)
        if (userLat && userLng) {
          setUserLatLng({lat:userLat, lng:userLng})
          // append new distance value each item to be used for sorting
          nodes.forEach(item => {
            const attr = item.attributes

            if (attr.field_latitude && attr.field_longitude) {
              const distance = getDistance(
                { latitude: userLat, longitude: userLng },
                { latitude: attr.field_latitude, longitude: attr.field_longitude },
              );

              attr.distance = distance === 0 ? 1 : distance;
            }
          })

          const bubbleSort = array => {
            const arr = Array.from(array);

            for (let i = 0; i < arr.length - 1; i++) {
              for (let j = 0; j < arr.length - 1 - i; j++) {
                // if distance value is undefined or greater than the next value, swap them
                if (!arr[j].attributes.distance || arr[j].attributes.distance > arr[j+1].attributes.distance) {
                  const temp = arr[j];
                  arr[j] = arr[j+1];
                  arr[j+1] = temp;
                }
              }
            }

            return arr;
          }

          const sortedNodes = bubbleSort(nodes);
          setSortedListings(sortedNodes)

          if(viewStyle !== 'map') {
            setListings(sortedNodes.slice(0, CONFIG.SHOW_ITEMS_NUMBER));
            if (sortedNodes.length > CONFIG.SHOW_ITEMS_NUMBER) {
              setMoreToDisplay(true);
            }
          } else {
            setListings(sortedNodes);
          }
        } else {
          setListings([]);
          setErrorMessage('Please enter a valid postcode.')
        }
      } else {
        setListings([]);
        setErrorMessage('Please enter a valid postcode.')
      }
    } else if(viewStyle === 'map') {
      const fetchedData = await recursiveFetch(query);
      if (fetchedData.length > 0) {
        setListings(fetchedData);
        setNextLink(null);
      }
    } else {
      const response = await fetch(query);
      const fetchedData = await response.json();

      if (fetchedData.data.length > 0) {
        setListings(fetchedData.data);

        if (fetchedData?.links?.next?.href) {
          setNextLink(fetchedData.links.next.href);
        } else {
          setNextLink(null);
        }
      } else {
        setListings([]);
        setNextLink(null);
        setErrorMessage('No results.')
      }
    }

    setLoading(false);
  }

  const fetchLoadMore = async () => {
    if (nextLink && !postcodeSearch) {
      const response = await fetch(nextLink);
      const fetchedData = await response.json();

      if (fetchedData.data.length > 0) {
        setListings(prevData => [...prevData, ...fetchedData.data]);

        if (fetchedData?.links?.next?.href) {
          setNextLink(fetchedData.links.next.href);
        } else {
          setNextLink(null)
        }
      }
    } else if (postcodeSearch && listings.length < sortedListings.length) {
      const currentItems = listings;

      setListings(sortedListings.slice(0, currentItems.length + CONFIG.SHOW_ITEMS_NUMBER));

      if (currentItems.length + CONFIG.SHOW_ITEMS_NUMBER >= sortedListings.length) {
        setMoreToDisplay(false)
      }
    }
  }

  useEffect(() => {
    fetchData();
  }, [loading]);

  // reset functionality
  const resetFilters = () => {
    setTextSearch(null);
    setPostcodeSearch(null);
    setIndustrySearch('All categories');
    setAgeSearch('All ages');

    setLoading(true);
  }

  useEffect(() => {
    if (
      textSearch ||
      postcodeSearch ||
      industrySearch !== 'All categories' ||
      ageSearch !== 'All ages' &&
      !filtersApplied
    ) {
      setFiltersApplied(true);
    } else if (filtersApplied) {
      setFiltersApplied(false);
    }
  }, [textSearch, postcodeSearch, industrySearch, ageSearch])

  if (loading) {
    return (
      <Container>
        <Row>
          <Col className="p-4">
            <div className="w-100">
              <Spinner animation="border" />
            </div>
          </Col>
        </Row>
      </Container>
    );
  }

  // ------------------------------
  // Handle the text search field
  const handleChange = e => {
    setTextQueryData({
      ...textQueryData,
      // Trimming any whitespace
      [e.target.name]: e.target.value.trim(),
    });
  };
  const handleQuerySubmit = e => {
    e.preventDefault();
    searchTextQuery(textQueryData);
  };
  const handleTextFieldSelect = e => {
    e.target.select();
  };
  function searchTextQuery(query) {
    const userInput = query.textQuery ?? '';
    setTextSearch(userInput);
    setLoading(true);
  }
  // ------------------------------

  // handle postcode search
  const handlePostcodeSubmit = e => {
    e.preventDefault();
    setLoading(true);
  }

  // Handle the Industry Select field
  const handleIndustrySelect = e => {
    e.preventDefault();
    setIndustrySearch(e.target.value);
    setLoading(true);
  };

  // Handle the Age Select field
  const handleAgeSelect = e => {
    e.preventDefault();
    setAgeSearch(e.target.value);
    setLoading(true);
  };

  const OrgModal = () => {
    const node_form = `${CONFIG.API_URL}${CONFIG.ADD_ORG_NODE_FORM}`;
    return (
      <Modal show={show} onHide={handleClose} className="orgModal">
        <Modal.Header>
          <h1>Add your organisation</h1>
        </Modal.Header>
        <Modal.Body>
          <iframe src={node_form} width="100%" height="100%" frameBorder="0" />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    );
  };

  return (
    <Container>
      <Row>

        <Col md={2}>
          <IndustrySelect
            label="Category"
            selected={industrySearch}
            onChange={handleIndustrySelect}
          />
        </Col>
        <Col md={2}>
          <AgeSelect
            label="Age range"
            selected={ageSearch}
            onChange={handleAgeSelect}
          />
        </Col>

        <Col md={5} lg={3}>
          <Form noValidate onSubmit={handlePostcodeSubmit}>
            <Form.Label>Postcode search</Form.Label>
            <div className="search-group postcode-search-group">
              <Form.Row>
                <Col xs={8}>
                  <Form.Control
                    type="text"
                    placeholder="Enter your postcode"
                    size="sm"
                    className="search-textfield postcode-search-textfield"
                    onChange={e => setPostcodeSearch(e.target.value.trim())}
                    onClick={handleTextFieldSelect}
                    name="postcodeQuery"
                    defaultValue={postcodeSearch}
                  />
                </Col>
                <Col xs={4}>
                  <Button type="submit">Search</Button>
                </Col>
              </Form.Row>
            </div>
          </Form>
        </Col>

        <Col md={5} lg={3}>
          <Form noValidate onSubmit={handleQuerySubmit}>
            <Form.Label>Search text</Form.Label>
            <div className="search-group">
              <Form.Row>
                <Col xs={8}>
                  <Form.Control
                    type="text"
                    placeholder="Enter your search"
                    size="sm"
                    className="search-textfield"
                    onChange={handleChange}
                    onClick={handleTextFieldSelect}
                    name="textQuery"
                    defaultValue={textSearch}
                  />
                </Col>
                <Col xs={4}>
                  <Button type="submit">Search</Button>
                </Col>
              </Form.Row>
            </div>
          </Form>
        </Col>

        <Col md={2} lg={2}>
          <ListViewSelector
            label="View"
            selected={viewStyle}
            viewStyleCallback={viewStyleCallback}
          />
        </Col>
      </Row>

      {filtersApplied && (
        <Row className="reset-filters">
          <Col>
            <button type="button" aria-label="reset search and filters" onClick={() => resetFilters()}>
              <img src={IconReset} alt="" />
              Reset filters
            </button>
          </Col>
        </Row>
      )}

      {listings &&
      viewStyle === 'map' ? (
        <MapDisplay
          userLatLng={userLatLng}
          allPosts={listings}
          postcodeSearch={postcodeSearch}
        />
      ) : (
        <ListItems
          viewStyle={viewStyle}
          allPosts={listings}
          nextLink={nextLink}
          fetchLoadMoreMethod={fetchLoadMore}
          postcodeSearch={postcodeSearch}
          moreToDisplay={moreToDisplay}
          errorMessage={errorMessage}
        />
      )
      }

      {/*
        * Temporarily disabled: #178816708
        *
        <Row className="footer-actions">
          <Col>
            <Button
              onClick={() => {
                handleShow();
              }}
            >
              Add Organisation
            </Button>
            <OrgModal />
          </Col>
        </Row>
      */}
    </Container>
  );
};

export default ListingDisplay;
